/* eslint-disable max-lines */
import { useInputState, useInputElement, usePrevious } from './hooks';
import { validateMaxLength, validateMaskPlaceholder, isInputFocused, getElementDocument, MaskUtils } from './utils';
import { useIsomorphicLayoutEffect } from '../hooks';
export const useMask = props => {
  const {
    alwaysShowMask = false,
    children,
    mask,
    maskPlaceholder = '_',
    beforeMaskedStateChange,
    value,
    ...restProps
  } = props;
  validateMaxLength(props);
  validateMaskPlaceholder(props);
  const maskUtils = new MaskUtils({
    mask,
    maskPlaceholder
  });
  const isMasked = !!mask;
  const isEditable = !restProps.disabled && !restProps.readOnly;
  const isControlled = value !== undefined;
  const previousIsMasked = usePrevious(isMasked);
  const initialValue = `${(isControlled ? value : props.defaultValue) || ''}`;
  const {
    inputRef,
    getInputState,
    setInputState,
    getLastInputState
  } = useInputState(initialValue, isMasked);
  const getInputElement = useInputElement(inputRef);

  function onChange(event) {
    const currentState = getInputState();
    const previousState = getLastInputState();
    let newInputState = maskUtils.processChange(currentState, previousState);

    if (beforeMaskedStateChange) {
      newInputState = beforeMaskedStateChange({
        currentState,
        previousState,
        nextState: newInputState
      });
    }

    setInputState(newInputState);

    if (props.onChange) {
      props.onChange(event, event.target.value);
    }
  }

  function onFocus(event) {
    // If autoFocus property is set, focus event fires before the ref handler gets called
    inputRef.current = event.target;
    const currentValue = getInputState().value;

    if (isMasked && !maskUtils.isValueFilled(currentValue)) {
      let newValue = maskUtils.formatValue(currentValue || '');
      let newSelection = maskUtils.getDefaultSelectionForValue(newValue);
      let newInputState = {
        value: newValue,
        selection: newSelection
      };

      if (beforeMaskedStateChange) {
        newInputState = beforeMaskedStateChange({
          currentState: getInputState(),
          nextState: newInputState
        });
        newValue = newInputState.value;
      }

      setInputState(newInputState);

      if (newValue !== currentValue && props.onChange) {
        props.onChange(event, event.target.value);
      } // Chrome resets selection after focus event, so we want to restore it later


      requestAnimationFrame(() => {
        setInputState(getLastInputState());
      });
    }

    if (props.onFocus) {
      props.onFocus(event);
    }
  }

  function onBlur(event) {
    const currentValue = getInputState().value;
    const lastValue = getLastInputState().value;

    if (isMasked && !alwaysShowMask && maskUtils.isValueEmpty(lastValue || '')) {
      let newValue = '';
      let newInputState = {
        value: newValue,
        selection: {
          start: null,
          end: null,
          length: 0
        }
      };

      if (beforeMaskedStateChange) {
        newInputState = beforeMaskedStateChange({
          currentState: getInputState(),
          nextState: newInputState
        });
        newValue = newInputState.value;
      }

      setInputState(newInputState);

      if (newValue !== currentValue && props.onChange) {
        props.onChange(event, event.target.value);
      }
    }

    if (props.onBlur) {
      props.onBlur(event);
    }
  }

  function onMouseDown(event) {
    const input = getInputElement();
    const {
      value: DOMvalue
    } = getInputState();
    const inputDocument = getElementDocument(input);

    if (!isInputFocused(input) && !maskUtils.isValueFilled(DOMvalue)) {
      const {
        clientX: mouseDownX,
        clientY: mouseDownY
      } = event;
      const mouseDownTime = new Date().getTime();

      const mouseUpHandler = mouseUpEvent => {
        inputDocument == null ? void 0 : inputDocument.removeEventListener('mouseup', mouseUpHandler);

        if (!isInputFocused(input)) {
          return;
        }

        const deltaX = Math.abs(mouseUpEvent.clientX - mouseDownX);
        const deltaY = Math.abs(mouseUpEvent.clientY - mouseDownY);
        const axisDelta = Math.max(deltaX, deltaY);
        const timeDelta = new Date().getTime() - mouseDownTime;

        if (axisDelta <= 10 && timeDelta <= 200 || axisDelta <= 5 && timeDelta <= 300) {
          const lastState = getLastInputState();
          const newSelection = maskUtils.getDefaultSelectionForValue(lastState.value);
          const newState = { ...lastState,
            selection: newSelection
          };
          setInputState(newState);
        }
      };

      inputDocument == null ? void 0 : inputDocument.addEventListener('mouseup', mouseUpHandler);
    }

    if (props.onMouseDown) {
      props.onMouseDown(event);
    }
  }

  if (isMasked && isControlled) {
    const input = getInputElement();
    const isFocused = input && isInputFocused(input);
    let newValue = isFocused || alwaysShowMask || value ? maskUtils.formatValue(value) : value;

    if (beforeMaskedStateChange) {
      newValue = beforeMaskedStateChange({
        nextState: {
          value: newValue,
          selection: {
            start: null,
            end: null,
            length: null
          }
        }
      }).value;
    }

    setInputState({ ...getLastInputState(),
      value: newValue
    });
  }

  const lastInputState = getLastInputState();
  const lastSelection = lastInputState.selection;
  useIsomorphicLayoutEffect(() => {
    if (!isMasked) {
      return;
    }

    const input = getInputElement();
    const isFocused = isInputFocused(input);
    const previousSelection = lastSelection;
    const currentState = getInputState();
    let newInputState = { ...currentState
    };

    if (!isControlled) {
      const currentValue = currentState.value;
      const formattedValue = maskUtils.formatValue(currentValue);
      const isValueEmpty = maskUtils.isValueEmpty(formattedValue);
      const shouldFormatValue = !isValueEmpty || isFocused || alwaysShowMask;

      if (shouldFormatValue) {
        newInputState.value = formattedValue;
      } else if (isValueEmpty && !isFocused) {
        newInputState.value = '';
      }
    }

    if (isFocused && !previousIsMasked) {
      newInputState.selection = maskUtils.getDefaultSelectionForValue(newInputState.value);
    } else if (isControlled && isFocused && previousSelection) {
      if (previousSelection.start !== null && previousSelection.end !== null) {
        newInputState.selection = previousSelection;
      }
    }

    if (beforeMaskedStateChange) {
      newInputState = beforeMaskedStateChange({
        currentState,
        nextState: newInputState
      });
    }

    setInputState(newInputState);
  });
  return { ...restProps,
    onFocus,
    onBlur,
    onChange: isMasked && isEditable ? onChange : props.onChange,
    onMouseDown: isMasked && isEditable ? onMouseDown : props.onMouseDown,
    value: isMasked && isControlled ? lastInputState.value : value
  };
};